import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import {
  User,
  Lock,
  Trash2,
  AlertCircle,
  CheckCircle,
  CreditCard,
  TrendingUp,
  ArrowRight,
  Check,
  Shield,
  BarChart3,
  Layers3
} from 'lucide-react';

const PLAN_CONFIG = {
  payg: {
    key: 'payg',
    name: 'PAYG',
    displayName: 'Pay As You Go',
    price: null,
    ratePerCall: 0.08,
    monthlyLimit: null,
    overageLabel: '£0.08 per API call',
    badgeLabel: 'No monthly commitment',
    description: 'Start immediately with no monthly commitment.',
    icon: Layers3,
    features: [
      'RegretRisk decision engine',
      'Full API access',
      'Usage dashboard',
      'No monthly minimum',
      '14-day free trial',
    ],
  },
  growth: {
    key: 'growth',
    name: 'Growth',
    displayName: 'Growth',
    price: 299,
    monthlyLimit: 10000,
    overageLabel: 'Overage at £0.05/call',
    badgeLabel: 'Most popular',
    description: 'Put booking decisions into production.',
    icon: Layers3,
    features: [
      'RegretRisk decision engine',
      '10,000 calls/month included',
      'Advanced analytics',
      'Webhook callbacks',
      'Priority support',
    ],
  },
  scale: {
    key: 'scale',
    name: 'Scale',
    displayName: 'Scale',
    price: 799,
    monthlyLimit: 50000,
    overageLabel: 'Overage at £0.025/call',
    badgeLabel: 'For revenue teams',
    description: 'Optimise booking performance at scale.',
    icon: BarChart3,
    features: [
      'Everything in Growth',
      '50,000 calls/month included',
      'White-label analytics',
      'Dedicated support',
      '99.9% uptime SLA',
    ],
  },
  enterprise: {
    key: 'enterprise',
    name: 'Enterprise',
    displayName: 'Enterprise',
    price: null,
    monthlyLimit: null,
    overageLabel: 'Custom commercial terms',
    badgeLabel: 'High-volume platforms',
    description: 'Decision intelligence infrastructure for complex deployments.',
    icon: Shield,
    features: [
      'Custom deployment options',
      'Security and compliance review',
      'Custom SLAs and contracts',
      'Volume pricing',
      'Dedicated commercial support',
    ],
  },
};

const PLAN_ORDER = ['payg', 'growth', 'scale', 'enterprise'];

function normalizeTier(rawTier) {
  if (!rawTier) return 'growth';
  if (rawTier === 'free' || rawTier === 'starter' || rawTier === 'professional') return 'growth';
  if (rawTier === 'performance') return 'scale';
  if (PLAN_CONFIG[rawTier]) return rawTier;
  return 'growth';
}

function formatPrice(planKey, rawTier) {
  const plan = PLAN_CONFIG[planKey];
  if (planKey === 'enterprise') return 'Custom pricing';
  if (planKey === 'payg') return '£0.08/call';
  return `£${plan.price.toLocaleString()}/month`;
}

function formatLimit(limit) {
  if (!limit) return 'Custom usage';
  return limit.toLocaleString();
}

export default function Account() {
  const navigate = useNavigate();
  const { user, profile, supabase } = useAuth();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');

  const [profileData, setProfileData] = useState({
    fullName: '',
    company: '',
    role: ''
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [billingData, setBillingData] = useState({
    rawTier: 'growth',
    tier: 'growth',
    monthlyLimit: 10000,
    currentUsage: 0,
    periodStart: null,
    periodEnd: null,
    overageRate: 0.05
  });

  useEffect(() => {
    if (profile) {
      setProfileData({
        fullName: profile.full_name || '',
        company: profile.company || '',
        role: profile.role || ''
      });
    }
  }, [profile]);

  useEffect(() => {
    if (user) {
      fetchBillingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchBillingData = async () => {
    try {
      const { data: tierData } = await supabase
        .from('user_tiers')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (tierData) {
        const normalizedTier = normalizeTier(tierData.tier);

        const { data: usageData } = await supabase
          .from('api_usage')
          .select('*')
          .eq('user_id', user.id)
          .gte('timestamp', tierData.period_start);

        setBillingData({
          rawTier: tierData.tier,
          tier: normalizedTier,
          monthlyLimit:
            normalizedTier === 'enterprise'
              ? tierData.monthly_limit || 0
              : PLAN_CONFIG[normalizedTier]?.monthlyLimit || tierData.monthly_limit || 0,
          currentUsage: usageData?.length || 0,
          periodStart: tierData.period_start,
          periodEnd: tierData.period_end,
          overageRate: tierData.overage_rate_gbp || 0
        });
      }
    } catch (err) {
      console.error('Error fetching billing data:', err);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setLoading(true);

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          full_name: profileData.fullName,
          company: profileData.company || null,
          role: profileData.role || null
        })
        .eq('id', user.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (err) {
      console.error('Error:', err);
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Password updated successfully' });
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      console.error('Error:', err);
      setMessage({ type: 'error', text: 'Failed to update password' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmEmail !== user.email) {
      setMessage({ type: 'error', text: 'Email does not match' });
      return;
    }

    setLoading(true);

    try {
      const { error: profileError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('id', user.id);

      if (profileError) throw profileError;

      await supabase.auth.signOut();
      navigate('/');
    } catch (err) {
      console.error('Error:', err);
      setMessage({ type: 'error', text: 'Failed to delete account' });
      setLoading(false);
    }
  };

  const usagePercent = billingData.monthlyLimit > 0
    ? Math.min((billingData.currentUsage / billingData.monthlyLimit) * 100, 100)
    : 0;

  const currentPlan = PLAN_CONFIG[billingData.tier] || PLAN_CONFIG.growth;
  const currentTierIndex = PLAN_ORDER.indexOf(billingData.tier);
  const nextTier = currentTierIndex < PLAN_ORDER.length - 1 ? PLAN_ORDER[currentTierIndex + 1] : null;
  const isTopTier = !nextTier;
  const nextPlan = nextTier ? PLAN_CONFIG[nextTier] : null;
  const isLegacyTrial = false;

  const usageStatusText = useMemo(() => {
    if (billingData.tier === 'enterprise') return 'Usage tracked against your custom plan';
    if (usagePercent >= 90) return 'Running low this cycle';
    if (usagePercent >= 70) return 'Approaching your included usage';
    return 'Within included usage';
  }, [billingData.tier, usagePercent]);

  const currentPlanIcon = currentPlan.icon;

  return (
    <div className="account-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@900&family=Montserrat:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root[data-theme="dark"] {
          --bg-primary: var(--bg-app);
          --bg-secondary: #141918;
          --bg-tertiary: #1F2624;
          --text-primary: #F4F1E8;
          --text-secondary: #C2BDB0;
          --text-muted: #8B8575;
          --accent-primary: #D97642;
          --accent-green: #5FD4A0;
          --accent-red: #EF6B6B;
          --border-primary: rgba(212, 196, 168, 0.15);
          --border-subtle: rgba(212, 196, 168, 0.1);
        }

        :root[data-theme="light"] {
          --bg-primary: #F8F6F1;
          --bg-secondary: #FFFFFF;
          --bg-tertiary: #E8E4DB;
          --text-primary: #1A1612;
          --text-secondary: #4A453C;
          --text-muted: #6B6558;
          --accent-primary: #D97642;
          --accent-green: #3BA876;
          --accent-red: #D94F4F;
          --border-primary: rgba(26, 22, 18, 0.15);
          --border-subtle: rgba(26, 22, 18, 0.1);
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .account-page {
          min-height: 100vh;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
        }

        .account-content {
          flex: 1;
          max-width: 1200px;
          margin: 0 auto;
          padding: 3rem 1.5rem;
          width: 100%;
        }

        .account-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 800;
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
        }

        .account-subtitle {
          color: var(--text-secondary);
          margin-bottom: 3rem;
        }

        .account-section {
          background: linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.015) 100%), var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 1rem;
          padding: 2rem;
          margin-bottom: 2rem;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        .section-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 1.25rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          font-weight: 500;
          margin-bottom: 0.5rem;
          color: var(--text-primary);
        }

        .form-input {
          width: 100%;
          padding: 0.75rem 1rem;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-primary);
          border-radius: 0.5rem;
          color: var(--text-primary);
          font-family: inherit;
          font-size: 1rem;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          font-family: inherit;
        }

        .btn-primary {
          background: var(--accent-primary);
          color: white;
        }

        .btn-primary:hover {
          background: #C56838;
          transform: translateY(-1px);
        }

        .btn-danger {
          background: var(--accent-red);
          color: white;
        }

        .btn-danger:hover {
          background: #DC5555;
        }

        .btn-secondary {
          background: var(--bg-tertiary);
          border: 1px solid var(--border-primary);
          color: var(--text-primary);
        }

        .btn-secondary:hover {
          border-color: var(--accent-primary);
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .message {
          padding: 1rem;
          border-radius: 0.5rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .message-success {
          background: rgba(95, 212, 160, 0.15);
          border: 1px solid var(--accent-green);
          color: var(--accent-green);
        }

        .message-error {
          background: rgba(239, 107, 107, 0.15);
          border: 1px solid var(--accent-red);
          color: var(--accent-red);
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal {
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 1rem;
          padding: 2rem;
          max-width: 500px;
          width: 100%;
        }

        .modal-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }

        .modal-text {
          color: var(--text-secondary);
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .billing-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1.5rem;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid var(--border-subtle);
        }

        .billing-current-tier {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
        }

        .billing-tier-icon-wrap {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(217, 118, 66, 0.1);
          border: 1px solid rgba(217, 118, 66, 0.25);
          color: var(--accent-primary);
          flex-shrink: 0;
        }

        .billing-tier-info h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
          font-family: 'Montserrat', sans-serif;
        }

        .billing-tier-subtitle {
          color: var(--text-secondary);
          font-size: 0.95rem;
          margin-bottom: 0.4rem;
        }

        .billing-tier-price {
          color: var(--text-muted);
          font-size: 0.9rem;
        }

        .billing-tier-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.38rem 0.75rem;
          border-radius: 999px;
          background: rgba(217, 118, 66, 0.12);
          border: 1px solid rgba(217, 118, 66, 0.24);
          color: var(--accent-primary);
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .billing-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .billing-stat-card {
          background: var(--bg-tertiary);
          padding: 1.25rem;
          border-radius: 0.75rem;
          border: 1px solid var(--border-subtle);
        }

        .billing-stat-label {
          font-size: 0.875rem;
          color: var(--text-muted);
          margin-bottom: 0.5rem;
        }

        .billing-stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .billing-stat-sublabel {
          font-size: 0.75rem;
          color: var(--text-secondary);
          margin-top: 0.25rem;
          line-height: 1.45;
        }

        .billing-usage-bar-container {
          margin-bottom: 2rem;
        }

        .billing-usage-bar-header {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .billing-usage-bar {
          height: 12px;
          background: var(--bg-tertiary);
          border-radius: 6px;
          overflow: hidden;
        }

        .billing-usage-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #5FD4A0 0%, #4AAF80 100%);
          border-radius: 6px;
          transition: width 0.3s ease;
        }

        .billing-usage-bar-fill.warning {
          background: linear-gradient(90deg, var(--accent-primary) 0%, #C56838 100%);
        }

        .billing-usage-bar-fill.critical {
          background: linear-gradient(90deg, var(--accent-red) 0%, #DC5555 100%);
        }

        .billing-note {
          margin-top: 0.75rem;
          font-size: 0.85rem;
          color: var(--text-muted);
          line-height: 1.5;
        }

        .upgrade-card {
          background: linear-gradient(135deg, rgba(217, 118, 66, 0.1) 0%, rgba(217, 118, 66, 0.05) 100%);
          border: 2px solid var(--accent-primary);
          border-radius: 0.9rem;
          padding: 2rem;
          margin-top: 2rem;
        }

        .upgrade-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .upgrade-card-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 0.3rem;
          font-family: 'Montserrat', sans-serif;
        }

        .upgrade-card-copy {
          color: var(--text-secondary);
          font-size: 0.92rem;
          line-height: 1.55;
        }

        .upgrade-card-price {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--accent-primary);
          white-space: nowrap;
        }

        .upgrade-features-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .upgrade-feature-column {
          background: var(--bg-secondary);
          padding: 1rem;
          border-radius: 0.65rem;
          border: 1px solid var(--border-subtle);
        }

        .upgrade-feature-column-title {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-muted);
          margin-bottom: 0.75rem;
          font-weight: 700;
        }

        .upgrade-feature-item {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          margin-bottom: 0.55rem;
          font-size: 0.9rem;
          line-height: 1.45;
          color: var(--text-secondary);
        }

        .upgrade-cta-button {
          width: 100%;
          padding: 1rem;
          background: var(--accent-primary);
          color: white;
          border-radius: 0.65rem;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 1rem;
        }

        .upgrade-cta-button:hover {
          background: #C56838;
          transform: translateY(-1px);
        }

        .enterprise-note {
          margin-top: 1.25rem;
          padding: 1rem 1.1rem;
          border-radius: 0.75rem;
          border: 1px solid var(--border-subtle);
          background: rgba(255,255,255,0.02);
          color: var(--text-secondary);
          line-height: 1.6;
          font-size: 0.92rem;
        }

        @media (max-width: 768px) {
          .account-content {
            padding: 2rem 1.5rem;
          }

          .account-title {
            font-size: 2rem;
          }

          .modal-actions {
            flex-direction: column-reverse;
          }

          .btn {
            width: 100%;
          }

          .billing-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .billing-usage-bar-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .upgrade-card-header {
            flex-direction: column;
          }

          .upgrade-features-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <Navigation />

      <div className="account-content">
        <h1 className="account-title">Account Settings</h1>
        <p className="account-subtitle">Manage your profile, usage, billing, and account preferences</p>

        {message.text && (
          <div className={`message message-${message.type}`}>
            {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span>{message.text}</span>
          </div>
        )}

        <div className="account-section">
          <h2 className="section-title">
            <CreditCard size={24} />
            Billing & Usage
          </h2>

          <div className="billing-header">
            <div className="billing-current-tier">
              <div className="billing-tier-icon-wrap">
                {React.createElement(currentPlanIcon, { size: 24, strokeWidth: 2 })}
              </div>

              <div className="billing-tier-info">
                <h3>{isLegacyTrial ? 'Professional Trial' : currentPlan.displayName}</h3>
                <div className="billing-tier-subtitle">{currentPlan.description}</div>
                <p className="billing-tier-price">{formatPrice(billingData.tier, billingData.rawTier)}</p>
              </div>
            </div>

            <div className="billing-tier-chip">
              {isLegacyTrial ? '14-day trial' : currentPlan.badgeLabel}
            </div>
          </div>

          <div className="billing-stats-grid">
            <div className="billing-stat-card">
              <div className="billing-stat-label">API Calls This Cycle</div>
              <div className="billing-stat-value">{billingData.currentUsage.toLocaleString()}</div>
              <div className="billing-stat-sublabel">
                {billingData.tier === 'enterprise'
                  ? 'Tracked against your custom plan'
                  : `of ${formatLimit(billingData.monthlyLimit)} included`}
              </div>
            </div>

            <div className="billing-stat-card">
              <div className="billing-stat-label">Calls Remaining</div>
              <div className="billing-stat-value">
                {billingData.monthlyLimit > 0
                  ? Math.max(billingData.monthlyLimit - billingData.currentUsage, 0).toLocaleString()
                  : 'Custom'}
              </div>
              <div className="billing-stat-sublabel">{usageStatusText}</div>
            </div>

            <div className="billing-stat-card">
              <div className="billing-stat-label">Commercial Model</div>
              <div className="billing-stat-value" style={{ fontSize: '1.125rem' }}>
                {billingData.tier === 'enterprise'
                  ? 'Custom'
                  : currentPlan.overageLabel}
              </div>
              <div className="billing-stat-sublabel">
                {billingData.tier === 'enterprise'
                  ? 'Tailored to your platform requirements'
                  : 'Designed to scale with production usage'}
              </div>
            </div>

            <div className="billing-stat-card">
              <div className="billing-stat-label">Billing Cycle</div>
              <div className="billing-stat-value" style={{ fontSize: '1.125rem' }}>
                {billingData.periodStart
                  ? new Date(billingData.periodStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                  : 'N/A'}
              </div>
              <div className="billing-stat-sublabel">
                Resets {billingData.periodEnd
                  ? new Date(billingData.periodEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                  : 'N/A'}
              </div>
            </div>
          </div>

          {billingData.tier !== 'enterprise' && (
            <div className="billing-usage-bar-container">
              <div className="billing-usage-bar-header">
                <span>Usage: {usagePercent.toFixed(1)}%</span>
                <span>
                  {billingData.currentUsage.toLocaleString()} / {billingData.monthlyLimit.toLocaleString()}
                </span>
              </div>

              <div className="billing-usage-bar">
                <div
                  className={`billing-usage-bar-fill ${usagePercent >= 90 ? 'critical' : usagePercent >= 70 ? 'warning' : ''}`}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>

              <div className="billing-note">
                {isLegacyTrial
                  ? 'You are currently on a Professional trial experience. Upgrade when you are ready to move into full production usage.'
                  : 'Your included usage resets each billing cycle. Higher tiers support larger production volumes and more tailored commercial terms.'}
              </div>
            </div>
          )}

          {!isTopTier && nextPlan && (
            <div className="upgrade-card">
              <div className="upgrade-card-header">
                <div>
                  <div className="upgrade-card-title">Move to {nextPlan.name}</div>
                  <div className="upgrade-card-copy">
                    {nextPlan.description} {nextPlan.monthlyLimit ? `Includes ${nextPlan.monthlyLimit.toLocaleString()} calls per month.` : 'Custom usage and commercial structure available.'}
                  </div>
                </div>

                <div className="upgrade-card-price">
                  {nextPlan.price ? `£${nextPlan.price.toLocaleString()}` : 'Custom'}
                  {nextPlan.price ? <span style={{ fontSize: '0.875rem', fontWeight: 400 }}>/mo</span> : null}
                </div>
              </div>

              <div className="upgrade-features-grid">
                <div className="upgrade-feature-column">
                  <div className="upgrade-feature-column-title">Your Current Plan</div>
                  {currentPlan.features.slice(0, 4).map((feature) => (
                    <div key={feature} className="upgrade-feature-item">
                      <Check size={16} color="#5FD4A0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="upgrade-feature-column">
                  <div className="upgrade-feature-column-title">What You Unlock</div>
                  {nextPlan.features.slice(0, 4).map((feature) => (
                    <div key={feature} className="upgrade-feature-item">
                      <TrendingUp size={16} color="var(--accent-primary)" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                className="upgrade-cta-button"
                onClick={() => navigate(nextTier === 'enterprise' ? '/contact' : `/signup?plan=${nextTier}`)}
              >
                {nextTier === 'enterprise' ? 'Talk to Sales' : `Upgrade to ${nextPlan.name}`}
                <ArrowRight size={20} />
              </button>
            </div>
          )}

          {billingData.tier === 'enterprise' && (
            <div className="enterprise-note">
              Enterprise plans are structured around integration depth, commercial requirements, deployment model, and support expectations. For billing adjustments or custom rollout support, contact the commercial team.
            </div>
          )}
        </div>

        <div className="account-section">
          <h2 className="section-title">
            <User size={24} />
            Profile Information
          </h2>

          <form onSubmit={handleUpdateProfile}>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                className="form-input"
                value={profileData.fullName}
                onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={user?.email || ''}
                disabled
                style={{ opacity: 0.6, cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Company (optional)</label>
              <input
                type="text"
                className="form-input"
                value={profileData.company}
                onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Role (optional)</label>
              <input
                type="text"
                className="form-input"
                value={profileData.role}
                onChange={(e) => setProfileData({ ...profileData, role: e.target.value })}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>

        <div className="account-section">
          <h2 className="section-title">
            <Lock size={24} />
            Change Password
          </h2>

          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-input"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                minLength={8}
                placeholder="At least 8 characters"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className="form-input"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                minLength={8}
                placeholder="Re-enter new password"
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>

        <div className="account-section" style={{ borderColor: 'var(--accent-red)' }}>
          <h2 className="section-title">
            <Trash2 size={24} />
            Danger Zone
          </h2>

          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Once you delete your account, there is no going back. Please be certain.
          </p>

          <button
            className="btn btn-danger"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete Account
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Delete Account</h3>
            <p className="modal-text">
              This action cannot be undone. All your data will be permanently deleted.
            </p>
            <p className="modal-text">
              Please type your email address <strong>{user?.email}</strong> to confirm:
            </p>

            <div className="form-group">
              <input
                type="email"
                className="form-input"
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                placeholder="Enter your email"
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmEmail('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteAccount}
                disabled={loading || deleteConfirmEmail !== user?.email}
              >
                {loading ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
